import { useState, useEffect } from "react";
import {
    Video, Clock, Search, MonitorPlay,
    CheckCircle2, XCircle, Hash,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ChevronUp, ChevronDown
} from "lucide-react";

import { showError } from "../../../../utils/toast";
import baseapi from "../../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";

interface CameraEntry {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    channel_id: number;
    camera_name: string;
    status: string;
    status_code: number;
    video_format: string;
}

interface NvrCameraTableProps {
    assetId: number;
    clientId: number;
}

export default function NvrCameraTable({ assetId, clientId }: NvrCameraTableProps) {
    const [data, setData] = useState<CameraEntry[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search and Sort state
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Debounce search input to avoid spamming the API
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            if (searchQuery !== debouncedSearch) {
                setCurrentPage(1); // Reset to page 1 only when search actually changes
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, debouncedSearch]);

    const fetchCameraData = async () => {
        if (!assetId) return;

        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;
            const queryObj: any = {};
            queryObj.asset_id = assetId;
            queryObj.client_id = clientId;
            // Build API parameters
            const params: any = {
                skip,
                limit,
                sort_by: "channel_id", // Specify which column to sort by
                sort_order: sortOrder  // 'asc' or 'desc'
            };

            // Add search parameter if it exists
            if (debouncedSearch.trim()) {
                queryObj.$or = [{ camera_name: { $ilike: `%${debouncedSearch.trim()}%` } }];
            }

            params.query = JSON.stringify(queryObj);

            const response = await baseapi.get("/nvr_camera", { params });

            if (response.data.status) {
                setData(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            } else {
                showError(response.data.message || "Failed to load camera data");
                setData([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "An error occurred while fetching cameras");
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch when dependencies change
    useEffect(() => {
        fetchCameraData();
    }, [assetId, currentPage, limit, debouncedSearch, sortOrder]);

    const handleSortToggle = () => {
        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    return (
        <div className="w-full flex flex-col gap-3 text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Video size={16} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white tracking-wide leading-tight">NVR Cameras</h2>
                        <p className="text-[9px] text-gray-200 uppercase tracking-widest mt-0.5">Channel Configuration</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Search Input */}
                    <div className="relative">
                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-200" />
                        <input
                            type="text"
                            placeholder="Search cameras..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-gray-900 border border-gray-700 text-[10px] rounded-md pl-7 pr-3 py-1.5 focus:outline-none focus:border-indigo-500 text-gray-200 w-48 transition-colors placeholder:text-gray-600"
                        />
                    </div>

                    {/* Total Channels Badge */}
                    <div className="text-[9px] font-bold text-gray-300 bg-gray-800/40 px-2.5 py-1.5 rounded-md border border-gray-700">
                        Total Channels: <span className="text-white">{totalRecords}</span>
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-lg overflow-hidden shadow-sm h-fit w-full">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800/80 border-b border-gray-700/50">
                            <tr>
                                {/* Sortable CH Header */}
                                <th
                                    className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap w-16 cursor-pointer hover:bg-gray-700/50 transition-colors select-none group"
                                    onClick={handleSortToggle}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <Hash size={12} className="text-gray-200" />
                                        CH
                                        <div className="text-indigo-400 opacity-60 group-hover:opacity-100 transition-opacity">
                                            {sortOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        </div>
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Video size={12} className="text-blue-400" />
                                        Camera Name
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <MonitorPlay size={12} className="text-purple-400" />
                                        Format
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    Status
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <Clock size={12} className="text-emerald-400" />
                                        Last Updated
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-gray-200 uppercase tracking-[0.2em]">Loading Cameras...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Search size={20} className="text-gray-200" />
                                            <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">
                                                {searchQuery ? "No results match your search" : "No cameras found"}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono font-bold text-gray-300 bg-gray-800/80 px-1.5 py-0.5 rounded border border-gray-700">
                                                {entry.channel_id < 10 ? `0${entry.channel_id}` : entry.channel_id}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-semibold text-gray-200">
                                                {entry.camera_name}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                                                {entry.video_format || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {entry.status === "active" ? (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                    <CheckCircle2 size={10} /> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                    <XCircle size={10} /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="text-[10px] font-medium text-gray-200">
                                                {new Date(entry.updated + "Z").toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {totalRecords > 0 && (
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/60 border-t border-gray-700">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                                <span className="text-gray-300">
                                    {(currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsLeft size={12} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronLeft size={12} />
                            </button>
                            <div className="px-1.5 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded text-[9px] font-bold mx-1">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronRight size={12} />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 transition-colors bg-gray-800 hover:bg-gray-700 rounded"
                            >
                                <ChevronsRight size={12} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}